import {
  Component,
  ElementRef,
  HostListener,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';

export interface DropdownOption {
  value: string;
  label: string;
  icon?: string;
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  @Input() label = 'Select';
  @Input() placeholder = 'Choose an option';
  @Input() options: DropdownOption[] = [];
  @Input() searchable = false;
  @Input() multiple = false;
  @Input() required = false;

  /**
   * Valeur par défaut à présélectionner au chargement.
   * Pour mode simple  : string  (ex: 'fr')
   * Pour mode multiple: string[] (ex: ['fr', 'en'])
   */
  @Input() defaultValue: string | string[] | null = null;

  /**
   * Verrouille la sélection : le trigger devient non-cliquable,
   * le bouton Clear est masqué et les options sont inaccessibles.
   */
  @Input() locked = false;

  @Output() selectionChange = new EventEmitter<
    DropdownOption | DropdownOption[] | null
  >();

  // ── UI state ─────────────────────────────────────────────
  isOpen = false;
  searchQuery = '';
  isTouched = false;

  // ── Valeurs sélectionnées ─────────────────────────────────
  @Input() selectedOption: DropdownOption | null = null;
  @Input() selectedOptions: DropdownOption[] = [];

  // ── ControlValueAccessor callbacks ───────────────────────
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  // Suivi pour ne pas écraser une valeur reçue via writeValue
  private hasExternalValue = false;

  constructor(private eRef: ElementRef) {}

  ngOnInit(): void {
    if (!this.selectedOption) {
      this.applyDefaultValue();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // ← Gérer selectedOption quand il change
    if (changes['selectedOption'] && this.selectedOption) {
      if (this.options?.length > 0) {
        const found = this.options.find(
          (o) => o.value === this.selectedOption!.value,
        );
        if (found) {
          this.selectedOption = found;
          // Pas besoin de onChange ici, c'est un @Input pas un formControl
        }
      } else {
        // Options pas encore là, mémoriser
        this.pendingValue = this.selectedOption;
      }
      return;
    }

    if (changes['options'] && this.options?.length > 0) {
      if (this.pendingValue !== null) {
        this.resolveValue(this.pendingValue);
        this.pendingValue = null;
      } else if (!this.hasExternalValue) {
        this.applyDefaultValue();
      }
    }

    if (changes['defaultValue'] && !this.hasExternalValue) {
      this.applyDefaultValue();
    }

    if (changes['locked'] && this.locked) {
      this.isOpen = false;
      this.searchQuery = '';
    }
  }

  // ── Valeur par défaut ────────────────────────────────────

  private applyDefaultValue(): void {
    if (this.defaultValue === null || this.defaultValue === undefined) return;
    this.resolveValue(this.defaultValue);
  }
  // ── ControlValueAccessor ─────────────────────────────────

  // Ajout d'une propriété pour mémoriser la valeur reçue avant que options soit chargé
  private pendingValue: any = null;

  writeValue(value: any): void {
    if (value !== null && value !== undefined) {
      this.hasExternalValue = true;
    }

    if (!value) {
      this.selectedOption = null;
      this.selectedOptions = [];
      this.pendingValue = null;
      return;
    }

    // Si options n'est pas encore chargé, on mémorise pour plus tard
    if (!this.options || this.options.length === 0) {
      this.pendingValue = value;
      return;
    }

    this.pendingValue = null;
    this.resolveValue(value);
  }
  // Extraire la logique de résolution dans une méthode dédiée
  private resolveValue(value: any): void {
    if (!this.options || this.options.length === 0) {
      this.pendingValue = value;
      return;
    }

    if (this.multiple) {
      const values: string[] = Array.isArray(value)
        ? value.map((v: any) => (typeof v === 'string' ? v : v.value))
        : [];
      this.selectedOptions = this.options.filter((o) =>
        values.includes(o.value),
      );
    } else {
      // Accepte string, ou objet {value, label}
      const val = typeof value === 'object' ? value?.value : value;
      this.selectedOption = this.options.find((o) => o.value === val) ?? null;
    }
  }
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {}

  // ── Validator ────────────────────────────────────────────

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.required) return null;
    const empty = this.multiple
      ? this.selectedOptions.length === 0
      : !this.selectedOption;
    return empty ? { required: true } : null;
  }

  // ── Getters ──────────────────────────────────────────────

  get displayValue(): string {
    if (this.multiple) {
      if (this.selectedOptions.length === 0) return '';
      if (this.selectedOptions.length === 1)
        return this.selectedOptions[0].label;
      return `${this.selectedOptions.length} selected`;
    }
    return this.selectedOption?.label ?? '';
  }

  get filteredOptions(): DropdownOption[] {
    if (!this.searchQuery.trim()) return this.options;
    return this.options.filter((o) =>
      o.label.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
  }

  get showError(): boolean {
    return this.isTouched && this.required && !this.displayValue;
  }

  isSelected(option: DropdownOption): boolean {
    if (this.multiple)
      return this.selectedOptions.some((o) => o.value === option.value);
    return this.selectedOption?.value === option.value;
  }

  // ── Interactions ─────────────────────────────────────────

  @HostListener('document:click')
  onClickOutside(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.searchQuery = '';
      this.isTouched = true;
      this.onTouched();
    }
  }

  toggle(event: Event): void {
    if (this.locked) return; // ← bloque l'ouverture si verrouillé
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.searchQuery = '';
      this.isTouched = true;
      this.onTouched();
    }
  }

  selectOption(option: DropdownOption, event: Event): void {
    if (this.locked) return; // ← sécurité supplémentaire
    event.stopPropagation();

    if (this.multiple) {
      const idx = this.selectedOptions.findIndex(
        (o) => o.value === option.value,
      );
      if (idx >= 0) this.selectedOptions.splice(idx, 1);
      else this.selectedOptions.push(option);
      const emitValue = this.selectedOptions.map((o) => o.value);
      this.onChange([...this.selectedOptions]); // ← objet complet
      this.selectionChange.emit([...this.selectedOptions]);
    } else {
      this.selectedOption = option;
      this.isOpen = false;
      this.searchQuery = '';
      this.isTouched = true;
      this.onChange(option); // ← objet complet
      this.onTouched();
      this.selectionChange.emit(option);
    }
    this.onValidatorChange();
  }

  clearSelection(event: Event): void {
    if (this.locked) return; // ← bloque le clear si verrouillé
    event.stopPropagation();
    this.selectedOption = null;
    this.selectedOptions = [];
    this.isTouched = true;
    this.onChange(this.multiple ? [] : null);
    this.onTouched();
    this.onValidatorChange();
    this.selectionChange.emit(this.multiple ? [] : null);
  }
}
